/**
 * TopUpModal - Wallet Top-up Modal
 *
 * Allows users to add funds to their wallet using
 * Easypaisa, JazzCash, or Bank Transfer.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wallet, Smartphone, Building2, Check, Loader2 } from 'lucide-react';
import { formatPKR, cn } from '../lib/utils';
import { API_BASE_URL } from '../lib/api-config';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (amount: number) => void;
  currentBalance: number;
}

type PaymentProvider = 'easypaisa' | 'jazzcash' | 'bank';

const PRESET_AMOUNTS = [500, 1000, 2000, 5000, 10000];

const PROVIDERS: { id: PaymentProvider; name: string; icon: React.ReactNode; color: string }[] = [
  {
    id: 'easypaisa',
    name: 'Easypaisa',
    icon: <Smartphone className="w-5 h-5" />,
    color: 'bg-green-500',
  },
  {
    id: 'jazzcash',
    name: 'JazzCash',
    icon: <Smartphone className="w-5 h-5" />,
    color: 'bg-red-500',
  },
  {
    id: 'bank',
    name: 'Bank Transfer',
    icon: <Building2 className="w-5 h-5" />,
    color: 'bg-blue-500',
  },
];

export function TopUpModal({ isOpen, onClose, onSuccess, currentBalance }: TopUpModalProps) {
  const [amount, setAmount] = useState<number>(1000);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [provider, setProvider] = useState<PaymentProvider>('easypaisa');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [step, setStep] = useState<'amount' | 'provider' | 'processing' | 'success'>('amount');

  const handleAmountSelect = (amt: number) => {
    setAmount(amt);
    setCustomAmount('');
    setError('');
  };

  const handleCustomAmountChange = (value: string) => {
    const numValue = parseInt(value.replace(/\D/g, ''), 10);
    setCustomAmount(value);
    if (!isNaN(numValue)) {
      setAmount(numValue);
    }
    setError('');
  };

  const handleProceed = () => {
    if (amount < 100) {
      setError('Minimum deposit is PKR 100');
      return;
    }
    if (amount > 500000) {
      setError('Maximum deposit is PKR 500,000');
      return;
    }
    setStep('provider');
  };

  const handleDeposit = async () => {
    setIsLoading(true);
    setStep('processing');
    setError('');

    try {
      const token = localStorage.getItem('boli_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/wallet/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount, provider }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Deposit failed');
      }

      setStep('success');
      setTimeout(() => {
        onSuccess(amount);
        handleClose();
      }, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to process deposit');
      setStep('provider');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('amount');
    setAmount(1000);
    setCustomAmount('');
    setProvider('easypaisa');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          className="relative w-full sm:max-w-md bg-background rounded-t-3xl sm:rounded-2xl shadow-xl overflow-hidden"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-bold text-foreground">Top Up Wallet</h2>
                <p className="text-xs text-muted-foreground">
                  Current: {formatPKR(currentBalance)}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-6">
            {step === 'amount' && (
              <>
                {/* Preset Amounts */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Select Amount
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {PRESET_AMOUNTS.map((amt) => (
                      <button
                        key={amt}
                        onClick={() => handleAmountSelect(amt)}
                        className={cn(
                          'py-3 rounded-xl font-semibold transition-all border',
                          amount === amt && !customAmount
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-muted/50 text-foreground border-border hover:border-primary/50'
                        )}
                      >
                        {formatPKR(amt)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Amount */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Or enter custom amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                      PKR
                    </span>
                    <input
                      type="text"
                      value={customAmount}
                      onChange={(e) => handleCustomAmountChange(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full pl-14 pr-4 py-3 rounded-xl border border-border bg-background text-foreground text-right font-display text-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Min: PKR 100 | Max: PKR 500,000
                  </p>
                </div>

                {error && (
                  <p className="text-sm text-red-500 text-center">{error}</p>
                )}

                {/* Proceed Button */}
                <button
                  onClick={handleProceed}
                  className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
                >
                  Continue with {formatPKR(amount)}
                </button>
              </>
            )}

            {step === 'provider' && (
              <>
                {/* Provider Selection */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-3 block">
                    Select Payment Method
                  </label>
                  <div className="space-y-2">
                    {PROVIDERS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setProvider(p.id)}
                        className={cn(
                          'w-full flex items-center gap-4 p-4 rounded-xl border transition-all',
                          provider === p.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        )}
                      >
                        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center text-white', p.color)}>
                          {p.icon}
                        </div>
                        <span className="flex-1 text-left font-medium text-foreground">
                          {p.name}
                        </span>
                        {provider === p.id && (
                          <Check className="w-5 h-5 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-bold text-foreground font-display">
                      {formatPKR(amount)}
                    </span>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-500 text-center">{error}</p>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('amount')}
                    className="flex-1 py-4 rounded-xl border border-border text-foreground font-semibold hover:bg-muted transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleDeposit}
                    disabled={isLoading}
                    className="flex-1 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    Pay Now
                  </button>
                </div>
              </>
            )}

            {step === 'processing' && (
              <div className="py-12 flex flex-col items-center gap-4">
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
                <p className="text-foreground font-medium">Processing payment...</p>
                <p className="text-sm text-muted-foreground">Please wait</p>
              </div>
            )}

            {step === 'success' && (
              <div className="py-12 flex flex-col items-center gap-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center"
                >
                  <Check className="w-8 h-8 text-green-600" />
                </motion.div>
                <p className="text-foreground font-bold text-lg">Deposit Successful!</p>
                <p className="text-muted-foreground">
                  {formatPKR(amount)} added to your wallet
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
